// Bausteine fuer die Beleg-Detailansicht: Erkennung (OCR), Zahlungsstand/Teilzahlung, Kontierung (SKR03), DATEV-Uebergabe und Nachweis.
import { useState, useEffect, useCallback, useRef } from "react";
import THEME from "../../theme";
import { fs } from "../finanzen/utils/styles";
import { belegeApi, belegeStufe } from "./belegeApi";
import KontierungEditor from "./KontierungEditor";
import { Chip, euro, datum, datumZeit, Klein } from "./ui";

const ENGINE = (e) => (e || "").split("+").map(t => ({ textebene: "Text im PDF", xml: "E-Rechnung (XML)", ocr_rapidocr: "lokale OCR", ocr_tesseract: "lokale OCR (Tesseract)" }[t] || t)).join(" + ");
const FELDNAMEN = { lieferant: "Lieferant", rechnungsnr: "Rechnungsnr.", rechnungsdatum: "Datum", netto: "Netto", ust: "USt", brutto: "Brutto", belegart: "Belegart", zahlungsart: "Zahlungsart", waehrung: "Währung" };

// Stufe basis: keine (automatische/manuelle) Erkennung, Angaben werden von Hand erfasst
export function ErkennungBox(props) { return belegeStufe.erweitert ? <ErkennungBoxInnen {...props} /> : null; }

function ErkennungBoxInnen({ beleg, onAenderung, setFehler }) {
  const [e, setE] = useState(undefined);
  const [alle, setAlle] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const laden = useCallback(() => { belegeApi.erkennung(beleg.id).then(setE).catch(() => setE(null)); }, [beleg.id]);
  useEffect(() => { laden(); }, [laden]);
  const starten = async () => {
    setFehler(""); setLaeuft(true);
    try { await belegeApi.erkennungStarten(beleg.id); laden(); await onAenderung(); } catch (x) { setFehler(x.message); } finally { setLaeuft(false); }
  };
  const gesperrt = ["an_datev_gesendet", "in_datev_bestaetigt"].includes(beleg.status);
  const farbe = !e ? THEME.colors.textMuted : e.konfidenz >= 80 ? THEME.colors.success : e.konfidenz >= 50 ? THEME.colors.warning : THEME.colors.danger;
  return (
    <div style={{ marginTop: 16, border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <strong>🔍 Automatische Erkennung</strong>
        {!gesperrt && <button style={{ ...fs.btnGray, padding: "6px 12px" }} disabled={laeuft} onClick={starten}>{laeuft ? "Läuft…" : e ? "Erneut erkennen" : "Jetzt erkennen"}</button>}
      </div>
      {e === undefined && <Klein>Lädt…</Klein>}
      {e === null && <Klein>Noch nicht erkannt. Die Erkennung füllt nur <b>leere</b> Felder – eigene Eingaben werden nie überschrieben.</Klein>}
      {e && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
            <div style={{ flex: 1, height: 8, borderRadius: 4, background: THEME.colors.surfaceMuted, overflow: "hidden" }}><div style={{ width: `${e.konfidenz}%`, height: "100%", background: farbe }} /></div>
            <span style={{ color: farbe }}>{e.konfidenz >= 80 ? "sicher" : e.konfidenz >= 50 ? "bitte prüfen" : "unsicher"} ({e.konfidenz} %)</span>
          </div>
          <Klein>Gelesen aus: {ENGINE(e.engine)} · {datumZeit(e.erstellt_am)}{e.uebernommen.length ? ` · übernommen: ${e.uebernommen.map(k => FELDNAMEN[k] || k).join(", ")}` : " · nichts Neues übernommen"}</Klein>
          {e.hinweise.map((h, i) => <Klein key={i} farbe={THEME.colors.warning}>⚠ {h}</Klein>)}
          <button style={{ ...fs.btnGray, padding: "4px 10px", marginTop: 8 }} onClick={() => setAlle(!alle)}>{alle ? "Erkannte Werte ausblenden" : "Erkannte Werte ansehen"}</button>
          {alle && (
            <div style={{ marginTop: 8, display: "grid", gap: 2, fontSize: THEME.fontSize.sm }}>
              {Object.entries(e.felder).filter(([k]) => !k.startsWith("_")).map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><span style={{ color: THEME.colors.textMuted }}>{FELDNAMEN[k] || k}</span><span>{String(v)} <span style={{ color: THEME.colors.textMuted }}>{e.feld_konfidenz[k] ? `(${e.feld_konfidenz[k]})` : ""}</span></span></div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ZahlungBox({ beleg, onAenderung, setFehler }) {
  const z = beleg.zahlungsstand;
  const [teil, setTeil] = useState(null);
  if (!z || z.brutto === null) return null;
  const gesperrt = ["an_datev_gesendet", "in_datev_bestaetigt"].includes(beleg.status);
  const aktion = async (fn) => { setFehler(""); try { await fn(); setTeil(null); await onAenderung(); } catch (e) { setFehler(e.message); } };
  const suchen = async () => { setFehler(""); try { setTeil((await belegeApi.teilzahlung(beleg.id)).vorschlaege); } catch (e) { setFehler(e.message); } };
  const anteil = z.brutto > 0 ? Math.min(100, ((z.bezahlt + z.skonto) / z.brutto) * 100) : 0;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: THEME.colors.surfaceMuted, overflow: "hidden" }}><div style={{ width: `${anteil}%`, height: "100%", background: z.vollstaendig ? THEME.colors.success : THEME.colors.accent }} /></div>
        <span>{z.vollstaendig ? "vollständig bezahlt" : z.teilweise ? "Teilzahlung" : "unbezahlt"}</span>
      </div>
      <Klein>Beleg {euro(z.brutto)} · bezahlt {euro(z.bezahlt)}{z.skonto ? ` · Skonto ${euro(z.skonto)}` : ""}{z.abweichung ? ` · akzeptierte Abweichung ${euro(z.abweichung)}` : ""} · offen {euro(z.offen)}</Klein>
      {z.abweichung ? <Klein farbe={THEME.colors.warning}>⚠ Differenz bewusst akzeptiert: {z.abweichung_grund}</Klein> : null}
      {!gesperrt && !z.vollstaendig && <button style={{ ...fs.btnGray, padding: "6px 12px", marginTop: 8 }} onClick={suchen}>Teilzahlungen / Raten suchen</button>}
      {teil && teil.length === 0 && <Klein>Keine passenden Zahlungen gefunden.</Klein>}
      {teil && teil.map((t, i) => (
        <div key={i} style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: 10, marginTop: 8 }}>
          <div><strong>{t.typ === "ratenzahlung" ? "Raten" : "Teilzahlung"}</strong> · Score {t.score} · Summe {euro(t.summe)}{t.offen_danach ? ` · danach offen ${euro(t.offen_danach)}` : ""}</div>
          {t.umsaetze.map(u => <Klein key={u.id}>{datum(u.datum)} · {euro(u.betrag_anteil)} · {u.partner || "–"} · {u.verwendungszweck}</Klein>)}
          <Klein>{t.begruendung}</Klein>
          <button style={{ ...fs.btn, marginTop: 8 }} onClick={() => aktion(() => belegeApi.bankBestaetigen(beleg.id, t.umsaetze.map(u => ({ bankumsatz_id: u.id, betrag_anteil: u.betrag_anteil })), { teilweise: t.typ === "teilzahlung", ergaenze: true }))}>
            {t.typ === "teilzahlung" ? "Als Teilzahlung bestätigen" : "Alle Raten bestätigen"}
          </button>
        </div>
      ))}
    </div>
  );
}

export function KontierungBox({ beleg, onAenderung, setFehler }) {
  const [k, setK] = useState(null);
  const [bearbeiten, setBearbeiten] = useState(false);
  const laden = useCallback(() => { belegeApi.kontierung(beleg.id).then(setK).catch(() => setK(null)); }, [beleg.id]);
  useEffect(() => { laden(); }, [laden]);
  const kannVorschlagen = beleg.lieferant && beleg.brutto !== null && !["abgelehnt", "dublette", "rueckfrage"].includes(beleg.status);
  const vorschlagen = async () => { setFehler(""); try { await belegeApi.kontierungVorschlag(beleg.id); laden(); } catch (e) { setFehler(e.message); } };
  const uebernehmen = async () => {
    setFehler("");
    try { await belegeApi.kontierungBestaetigen(beleg.id, k.vorschlag.map(z => ({ konto: z.konto, gegenkonto: z.gegenkonto, bu_schluessel: z.bu_schluessel, betrag_brutto: z.betrag_brutto, buchungstext: z.buchungstext, kostenstelle: z.kostenstelle, steuersatz: z.steuersatz }))); laden(); await onAenderung(); }
    catch (e) { setFehler(e.message); }
  };
  if (!k) return null;
  const nurAnzeige = !belegeStufe.erweitert;   // Stufe basis: Vorschlaege berechnen und ansehen, aber nichts bestaetigen oder von Hand kontieren
  const hinweise = [...new Set(k.vorschlag.flatMap(z => z.hinweise))];
  const Zeilen = ({ liste }) => liste.map(z => (
    <div key={z.id} style={{ borderTop: `1px solid ${THEME.colors.border}`, padding: "6px 0" }}>
      <div><strong>{z.konto}</strong> {z.konto_bezeichnung ? `– ${z.konto_bezeichnung}` : ""} <span style={{ color: THEME.colors.textMuted }}>an</span> {z.gegenkonto} · BU {z.bu_schluessel || "–"} · {euro(z.betrag_brutto)}</div>
      <Klein>{z.buchungstext}{z.begruendung ? ` · ${z.begruendung}` : ""}{z.kostenstelle ? ` · KOST ${z.kostenstelle}` : ""}</Klein>
    </div>
  ));
  return (
    <div style={{ marginTop: 18 }}>
      <h3 style={{ fontSize: THEME.fontSize.md, margin: "0 0 8px" }}>Kontierung (SKR03) {k.bestaetigt.length > 0 && <Chip text="bestätigt" farbe={THEME.colors.success} />} {k.exportiert && <Chip text="exportiert" farbe={THEME.colors.warning} />}</h3>
      {k.bestaetigt.length > 0 && !bearbeiten && (
        <>
          <Zeilen liste={k.bestaetigt} />
          {!k.exportiert && !nurAnzeige && <button style={{ ...fs.btnGray, padding: "6px 12px", marginTop: 8 }} onClick={() => setBearbeiten(true)}>Ändern</button>}
          {k.exportiert && <Klein>Bereits in einem EXTF-Export – zum Ändern zuerst den Export stornieren (Reiter DATEV).</Klein>}
        </>
      )}
      {k.bestaetigt.length === 0 && !bearbeiten && (
        <>
          {k.vorschlag.length > 0 ? (
            <>
              <Klein>Vorschlag (noch nicht bestätigt – wird nicht exportiert):</Klein>
              {nurAnzeige && <Klein farbe={THEME.colors.warning}>Nur zur Ansicht: Bestätigen und Buchen sind freigeschaltet, sobald die Kontenzuordnung und die Steuerkennzeichen vom Steuerberater bestätigt sind.</Klein>}
              <Zeilen liste={k.vorschlag} />
              {hinweise.map((h, i) => <Klein key={i} farbe={THEME.colors.warning}>⚠ {h}</Klein>)}
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                {!nurAnzeige && <button style={fs.btn} onClick={uebernehmen}>✔ Vorschlag übernehmen</button>}
                {!nurAnzeige && <button style={fs.btnGray} onClick={() => setBearbeiten(true)}>Bearbeiten</button>}
                <button style={fs.btnGray} onClick={vorschlagen}>Neu berechnen</button>
              </div>
            </>
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={fs.btnGray} disabled={!kannVorschlagen} onClick={vorschlagen}>Buchungsvorschlag berechnen</button>
              {!nurAnzeige && <button style={fs.btnGray} disabled={beleg.brutto === null} onClick={() => setBearbeiten(true)}>Von Hand kontieren</button>}
              {!kannVorschlagen && <Klein>Für einen Vorschlag werden Lieferant und Brutto-Betrag benötigt.</Klein>}
            </div>
          )}
        </>
      )}
      {bearbeiten && <KontierungEditor beleg={beleg} start={k.bestaetigt.length ? k.bestaetigt : k.vorschlag} setFehler={setFehler} onAbbrechen={() => setBearbeiten(false)} onBestaetigt={() => { setBearbeiten(false); laden(); onAenderung(); }} />}
    </div>
  );
}

// Stufe basis: kein DATEV-Versand
export function DatevBox(props) { return belegeStufe.erweitert ? <DatevBoxInnen {...props} /> : null; }

function DatevBoxInnen({ beleg, onAenderung, setFehler, geheZu }) {
  const [info, setInfo] = useState(null);
  const [uebergaben, setUebergaben] = useState([]);
  const [referenz, setReferenz] = useState("");
  const [hinweis, setHinweis] = useState("");
  const nachweisRef = useRef(null);
  const laden = useCallback(() => { belegeApi.datevStatus().then(s => setUebergaben(s.uebergaben.filter(u => u.beleg_id === beleg.id))).catch(() => {}); }, [beleg.id]);
  useEffect(() => { laden(); }, [laden]);
  if (!["bankumsatz_zugeordnet", "an_datev_gesendet", "in_datev_bestaetigt"].includes(beleg.status)) return null;
  const aktion = async (fn, text) => { setFehler(""); setHinweis(""); try { await fn(); if (text) setHinweis(text); laden(); await onAenderung(); } catch (e) { setFehler(e.message); } };
  const nachweisHochladen = (datei) => { if (!datei) return; const fd = new FormData(); fd.append("datei", datei, datei.name); aktion(() => belegeApi.nachweis(beleg.id, fd), "Nachweis archiviert – Beleg ist „In DATEV bestätigt“."); };
  return (
    <div style={{ marginTop: 18 }}>
      <h3 style={{ fontSize: THEME.fontSize.md, margin: "0 0 8px" }}>DATEV</h3>
      {beleg.status === "bankumsatz_zugeordnet" && (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={fs.btnGray} onClick={async () => { try { setInfo(await belegeApi.datevVorschau(beleg.id)); } catch (e) { setFehler(e.message); } }}>Vorschau der Upload-Mail</button>
            <button style={fs.btn} onClick={() => aktion(() => belegeApi.datevVorbereiten([beleg.id]).then(r => { if (r.ergebnisse[0].fehler) throw new Error(r.ergebnisse[0].fehler); }), "Für den Versand vorbereitet – Freigabe und Senden im Reiter „DATEV“.")}>Für Versand vorbereiten</button>
            {geheZu && <button style={fs.btnGray} onClick={() => geheZu("datev")}>Zum Reiter DATEV</button>}
          </div>
          {info && <Klein>Würde senden: {info.dateiname} ({Math.round(info.groesse_bytes / 1024)} KB) · Modus: <b>{info.modus}</b>. {info.hinweis}{info.zu_gross ? " ⚠ Mail zu groß für den Versand." : ""}</Klein>}
          <div style={{ marginTop: 10 }}>
            <Klein>Beleg wurde ohne ComeFit direkt in DATEV hochgeladen? Referenz erfassen (es wird nichts gesendet):</Klein>
            <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
              <input style={{ ...fs.input, flex: "1 1 220px" }} placeholder="z. B. Direkt in DATEV hochgeladen am 22.10." value={referenz} onChange={e => setReferenz(e.target.value)} />
              <button style={fs.btnGray} disabled={referenz.trim().length < 3} onClick={() => aktion(() => belegeApi.datevManuell(beleg.id, referenz.trim()), "Als „An DATEV gesendet“ vermerkt.")}>Als übergeben vermerken</button>
            </div>
          </div>
        </>
      )}
      {beleg.status === "an_datev_gesendet" && (
        <>
          <Klein>Gesendet bedeutet nur: der Mailserver hat die Nachricht angenommen. „In DATEV bestätigt“ gibt es erst mit archiviertem Nachweis (z. B. Bildschirmfoto der DATEV-Belegansicht, Bestätigung der Kanzlei).</Klein>
          <input ref={nachweisRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={e => { nachweisHochladen(e.target.files[0]); e.target.value = ""; }} />
          <button style={{ ...fs.btn, marginTop: 8 }} onClick={() => nachweisRef.current?.click()}>Nachweis hochladen → „In DATEV bestätigt“</button>
        </>
      )}
      {beleg.status === "in_datev_bestaetigt" && <Klein farbe={THEME.colors.success}>✔ Mit archiviertem Nachweis bestätigt.</Klein>}
      {uebergaben.map(u => (
        <div key={u.id} style={{ borderTop: `1px solid ${THEME.colors.border}`, marginTop: 8, paddingTop: 6 }}>
          <div>Sendung #{u.id} <Chip text={u.status} farbe={u.status === "gesendet" ? THEME.colors.success : u.status === "fehlgeschlagen" ? THEME.colors.danger : THEME.colors.warning} /> <span style={{ color: THEME.colors.textMuted }}>Modus {u.modus}</span></div>
          {u.ereignisse.map((e, i) => <Klein key={i}>{datumZeit(e.zeitpunkt)} · {e.ereignis}{e.details ? ` – ${e.details}` : ""}</Klein>)}
        </div>
      ))}
      {hinweis && <Klein farbe={THEME.colors.success}>{hinweis}</Klein>}
    </div>
  );
}
